import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import DynamicForm from "../stories/Forms/DynamicForm";
import {
  changePasswordSchema,
  vhChangePasswordSchema,
} from "../stories/Forms/validationSchema";
import { Container, Col, Row, Spinner } from "react-bootstrap";
import {
  changePasswordFields,
  changePasswordFieldsVH,
  links,
} from "../stories/Forms/fields";
import PageLayout from "../components/Layout/PageLayout";
import useApis from "../services/useAPI";
import { useNavigate } from "react-router";
import { useAuth } from "../components/AuthContext/AuthContext";
import { logoutUrl, changePasswordUrl } from "../utils/constants";
import LoggedInLayout from "../stories/LoggedInScreens/LoggedInLayout";
import logo from "../assets/images/boots-myhealthchecked-logo.svg";
import img from "../assets/images/notification.svg";
import { BoxArrowInRight, XCircleFill, CheckCircleFill } from "react-bootstrap-icons";

// Component for handling change password functionality
function ChangePassword() {
  const navigate = useNavigate();
  const auth = useAuth();
  auth?.setTitle("Change password");
  const formikRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API call hook with various states
  const { callApi, serverError, isLoading, showSuccess, tokenError, message } =
    useApis();

  // Function to handle form submission
  const handleSubmit = async (data) => {
    if (auth?.featureFlag) {
      setIsSubmitting(true);
      try {
        await axios({
          url: changePasswordUrl,
          method: "PUT",
          data,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token") || auth.accessToken}`,
            "Content-Type": "application/json",
          },
        });
        auth.logout();
        await callApi(null, logoutUrl, "POST");
        navigate("/login");
        auth?.showToast(
          "Success",
          "Your password has been changed successfully. Please log in with your new password.",
          "../assets/mhc.png",
          {
            icon: <BoxArrowInRight />,
            className: auth?.featureFlag && "vh-toaster",
          },
        );
      } catch (err) {
        const backendMessage = err?.response?.data?.error;
        if (
          backendMessage &&
          /current password is incorrect/i.test(backendMessage)
        ) {
          formikRef.current?.setFieldError(
            "current_password",
            "Current password is incorrect",
          );
          formikRef.current?.setFieldTouched("current_password", true, false);
        } else {
          auth?.showToast(
            "Error",
            backendMessage || "Something went wrong. Please try again.",
            "../assets/mhc.png",
            {
              icon: <BoxArrowInRight />,
              className: auth?.featureFlag && "vh-toaster",
            },
          );
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    await callApi(data, changePasswordUrl, "PUT", {
      Authorization: `Bearer ${localStorage.getItem("access_token") || auth.accessToken}`,
      "Content-Type": "application/json",
    });
  };

  // Effect to handle post password change actions
  useEffect(() => {
    if (message.postMessage && showSuccess) {
      auth?.showToast(
        "Success",
        "Password changed successfully!",
        "../assets/mhc.png",
        {
          icon: <CheckCircleFill />,
          className: auth?.featureFlag && "vh-toaster",
        },
      );
      auth.logout();
      callApi(null, logoutUrl, "POST");
      navigate("/login");
    }
  }, [message.postMessage, showSuccess, navigate, auth, callApi]);

  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast("Error", tokenError || serverError, "../assets/mhc.png", {
        icon: <XCircleFill />,
        className: auth?.featureFlag && "vh-toaster",
      });
    }
  }, [serverError, tokenError, message]);

  const oldContent = (
    <Container>
      <Row>
        <Col md={6}>
          <div className="reset-password-card card">
            <DynamicForm
              fields={changePasswordFields}
              isLoading={isLoading}
              onSubmit={handleSubmit}
              validationSchema={changePasswordSchema}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );

  const newContent = (
    <Container fluid>
      <Row>
        <Col md={12} lg={12} className="px-0">
          <div className="vh-reset-password-card card">
            <DynamicForm
              formikRef={formikRef}
              fields={changePasswordFieldsVH}
              isLoading={isSubmitting}
              onSubmit={handleSubmit}
              validationSchema={vhChangePasswordSchema}
              isChangePassword={true}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );

  if (auth.featureFlagLoading)
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );

  return (
    <LoggedInLayout
      buttonLabel="Logout"
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      vitalityLayout={auth?.featureFlag}
    >
      {auth?.featureFlag ? (
        <PageLayout
          heading="Change password"
          subHeading="Update your password to keep your account secure."
        >
          {newContent}
        </PageLayout>
      ) : (
        oldContent
      )}
    </LoggedInLayout>
  );
}

export default ChangePassword;
